import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Home,
  Briefcase,
  Search as SearchIcon,
  MessageCircle,
  Users,
  User,
  Bookmark,
  FileText,
  Calendar,
  Bell,
  Settings,
  LogOut,
  BarChart3,
  DollarSign,
  ShieldCheck,
  Video,
  Sparkles,
  Building2,
  Mail,
  Info,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/lib/supabase";

import AccountDropdown from "./AccountDropdown";

export default function RightRail() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [showAccountDropdown, setShowAccountDropdown] = useState(false);

  const isStudent = (user?.profile_type || "").toLowerCase() === "student";
  const isUniversity = (user?.profile_type || "").toLowerCase() === "university";

  // Check if we are on a "System" page where the icon should be gray
  const isSystemPage =
    location.pathname.startsWith("/my-profile");

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      if (showAccountDropdown && !target.closest(".account-dropdown-container")) {
        setShowAccountDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showAccountDropdown]);

  const active = {
    home: location.pathname.startsWith("/home"),
    opportunities: location.pathname.startsWith("/opportunities"),
    jobs: location.pathname.startsWith("/jobs"),
    events: location.pathname.startsWith("/events"),
    search: location.pathname.startsWith("/search"),
    community: location.pathname.startsWith("/community"),
    invideo: location.pathname.startsWith("/invideo"),
  };

  const initial = (user?.name?.charAt(0)?.toUpperCase() || "U");

  return (
    <aside className="hidden lg:block w-24 bg-white border-l border-gray-200 fixed right-0 top-0 h-full z-50">
      <div className="flex flex-col items-center h-full pt-6 pb-6 w-full">
        {/* Account - Top */}
        <div className="flex flex-col items-center space-y-2">
          <div className="relative">
            <button
              onClick={() => setShowAccountDropdown(!showAccountDropdown)}
              className={`h-10 w-10 rounded-full flex items-center justify-center hover:shadow-xl transition-all duration-200 ring-2 ring-white shadow-lg bg-blue-600`}
              title="Account"
            >
              <span className="text-sm font-semibold text-white">{initial}</span>
            </button>

            {/* Account Dropdown */}
            {showAccountDropdown && (
              <AccountDropdown
                onClose={() => setShowAccountDropdown(false)}
                user={user}
              />
            )}
          </div>
        </div>

        <div className="flex-1 flex flex-col justify-around w-full items-center">
          {/* Home */}
          <div className="flex flex-col items-center space-y-2">
            <button
              onClick={() => navigate("/home")}
              className={`p-3 rounded-lg transition-colors ${active.home ? "bg-gray-100" : "hover:bg-gray-100"}`}
              title="Home"
            >
              <Home className={`h-6 w-6 ${active.home ? "text-trestune-primary" : "text-gray-700"}`} />
            </button>
            <span className={`text-xs text-center ${active.home ? "text-trestune-primary font-medium" : "text-gray-600"}`}>
              Home
            </span>
          </div>

          {/* Opportunities */}
          <div className="flex flex-col items-center space-y-2">
            <button
              onClick={() => navigate("/opportunities")}
              className={`p-3 rounded-lg transition-colors ${active.opportunities ? "bg-gray-100" : "hover:bg-gray-100"}`}
              title="Opportunities"
            >
              <Briefcase className={`h-6 w-6 ${active.opportunities ? "text-trestune-primary" : "text-gray-700"}`} />
            </button>
            <span className={`text-xs text-center ${active.opportunities ? "text-trestune-primary font-medium" : "text-gray-600"}`}>
              Opportunities
            </span>
          </div>

          {/* InVideo */}
          <div className="flex flex-col items-center space-y-2">
            <button
              onClick={() => navigate("/invideo")}
              className={`p-3 rounded-lg transition-colors ${active.invideo ? "bg-gray-100" : "hover:bg-gray-100"}`}
              title="InVideo"
            >
              <Video className={`h-6 w-6 ${active.invideo ? "text-trestune-primary" : "text-gray-700"}`} />
            </button>
            <span className={`text-xs text-center ${active.invideo ? "text-trestune-primary font-medium" : "text-gray-600"}`}>
              InVideo
            </span>
          </div>

          {/* Events */}
          <div className="flex flex-col items-center space-y-2">
            <button
              onClick={() => navigate("/events")}
              className={`p-3 rounded-lg transition-colors ${active.events ? "bg-gray-100" : "hover:bg-gray-100"}`}
              title="Events"
            >
              <Calendar className={`h-6 w-6 ${active.events ? "text-trestune-primary" : "text-gray-700"}`} />
            </button>
            <span className={`text-xs text-center ${active.events ? "text-trestune-primary font-medium" : "text-gray-600"}`}>
              Events
            </span>
          </div>

          {/* Jobs */}
          <div className="flex flex-col items-center space-y-2">
            <button
              onClick={() => navigate("/jobs")}
              className={`p-3 rounded-lg transition-colors ${active.jobs ? "bg-gray-100" : "hover:bg-gray-100"}`}
              title="Jobs"
            >
              <Building2 className={`h-6 w-6 ${active.jobs ? "text-trestune-primary" : "text-gray-700"}`} />
            </button>
            <span className={`text-xs text-center ${active.jobs ? "text-trestune-primary font-medium" : "text-gray-600"}`}>
              Jobs
            </span>
          </div>
        </div>


      </div>
    </aside>
  );
}
